/**
 * Permission Service for PBS Frontend
 * Handles role-based access control and permission checks
 */

import apiClient from './apiClient';
import authService, { User } from './authService';

// ============================================================================
// PERMISSION CONSTANTS (matches backend)
// ============================================================================

export enum Permission {
  // Allocation Permissions
  VIEW_ALLOCATIONS = 'view_allocations',
  ADD_FUTURE_ALLOCATIONS = 'add_future_allocations',
  MODIFY_FUTURE_ALLOCATIONS = 'modify_future_allocations',
  DELETE_FUTURE_ALLOCATIONS = 'delete_future_allocations',
  MODIFY_PAST_ALLOCATIONS = 'modify_past_allocations',
  DELETE_PAST_ALLOCATIONS = 'delete_past_allocations',
  
  // Revenue Permissions
  VIEW_REVENUE = 'view_revenue',
  ENTER_REVENUE = 'enter_revenue',
  MODIFY_REVENUE = 'modify_revenue',
  
  // Cost Permissions
  VIEW_COSTS = 'view_costs',
  VIEW_ALL_COSTS = 'view_all_costs',
  
  // Dashboard Permissions
  VIEW_DASHBOARD = 'view_dashboard',
  VIEW_PROJECT_HEALTH = 'view_project_health',
  VIEW_SAVED_DASHBOARDS = 'view_saved_dashboards',
  
  // Project Permissions
  VIEW_OWN_PROJECTS = 'view_own_projects',
  VIEW_ALL_PROJECTS = 'view_all_projects',
  
  // Member Permissions
  ADD_MEMBERS = 'add_members',
  MODIFY_MEMBERS = 'modify_members',
  DELETE_MEMBERS = 'delete_members',
  
  // Admin Permissions
  ADMIN = 'admin',
}

export enum Role {
  PROJECT_OWNER = 'project_owner',
  BU_HEAD = 'bu_head',
  OPERATIONS = 'operations',
  ACCOUNTS = 'accounts',
  ADMIN = 'admin',
}

interface PermissionsResponse {
  success: boolean;
  data: {
    role: string;
    permissions: string[];
    all_roles: string[];
    pbsRoles?: string[];
  };
}

class PermissionService {
  private permissions: string[] = [];
  private role: string = Role.PROJECT_OWNER;
  private pbsRoles: string[] = [];
  private permissionsLoaded: boolean = false;

  /**
   * Load permissions from backend
   */
  async loadPermissions(): Promise<void> {
    try {
      const response = await apiClient.get<PermissionsResponse>('/auth/permissions');
      
      if (response.data.success) {
        this.permissions = response.data.data.permissions;
        this.role = response.data.data.role;
        this.pbsRoles = response.data.data.pbsRoles || [];
        this.permissionsLoaded = true;
      }
    } catch (error) {
      console.error('Failed to load permissions:', error);
      // Fall back to user data from storage
      const user = authService.getUserData();
      if (user) {
        this.role = user.role || Role.PROJECT_OWNER;
        this.pbsRoles = user.pbsRoles || [];
      }
    }
  }

  /**
   * Check if user has a specific permission
   */
  hasPermission(permission: Permission | string): boolean {
    if (permission === Permission.ADMIN && this.hasPbsRole('admin')) {
      return true;
    }
    
    if (!this.permissionsLoaded) {
      // If permissions not loaded, check based on role
      return this.hasPermissionByRole(permission);
    }
    
    return this.permissions.includes(permission);
  }

  /**
   * Check if user has any of the specified permissions
   */
  hasAnyPermission(permissions: (Permission | string)[]): boolean {
    return permissions.some(perm => this.hasPermission(perm));
  }

  /**
   * Check if user has all of the specified permissions
   */
  hasAllPermissions(permissions: (Permission | string)[]): boolean {
    return permissions.every(perm => this.hasPermission(perm));
  }

  /**
   * Check permission based on role (fallback when permissions not loaded)
   */
  private hasPermissionByRole(permission: Permission | string): boolean {
    const rolePermissions = this.getRolePermissions(this.role);
    return rolePermissions.includes(permission as string);
  }

  /**
   * Get permissions for a specific role
   */
  private getRolePermissions(role: string): string[] {
    // This matches the backend ROLE_PERMISSIONS mapping
    const rolePermissionsMap: Record<string, string[]> = {
      [Role.PROJECT_OWNER]: [
        Permission.VIEW_ALLOCATIONS,
        Permission.ADD_FUTURE_ALLOCATIONS,
        Permission.MODIFY_FUTURE_ALLOCATIONS,
        Permission.DELETE_FUTURE_ALLOCATIONS,
        Permission.VIEW_REVENUE,
        Permission.ENTER_REVENUE,
        Permission.MODIFY_REVENUE,
        Permission.VIEW_COSTS,
        Permission.VIEW_DASHBOARD,
        Permission.VIEW_SAVED_DASHBOARDS,
        Permission.VIEW_OWN_PROJECTS,
        Permission.ADD_MEMBERS,
        Permission.MODIFY_MEMBERS,
        Permission.DELETE_MEMBERS,
      ],
      [Role.BU_HEAD]: [
        Permission.VIEW_ALLOCATIONS,
        Permission.VIEW_REVENUE,
        Permission.ENTER_REVENUE,
        Permission.MODIFY_REVENUE,
        Permission.VIEW_COSTS,
        Permission.VIEW_ALL_COSTS,
        Permission.VIEW_DASHBOARD,
        Permission.VIEW_PROJECT_HEALTH,
        Permission.VIEW_SAVED_DASHBOARDS,
        Permission.VIEW_ALL_PROJECTS,
      ],
      [Role.OPERATIONS]: [
        Permission.VIEW_ALLOCATIONS,
        Permission.ADD_FUTURE_ALLOCATIONS,
        Permission.MODIFY_FUTURE_ALLOCATIONS,
        Permission.DELETE_FUTURE_ALLOCATIONS,
        Permission.MODIFY_PAST_ALLOCATIONS,
        Permission.DELETE_PAST_ALLOCATIONS,
        Permission.VIEW_REVENUE,
        Permission.ENTER_REVENUE,
        Permission.MODIFY_REVENUE,
        Permission.VIEW_COSTS,
        Permission.VIEW_ALL_COSTS,
        Permission.VIEW_DASHBOARD,
        Permission.VIEW_PROJECT_HEALTH,
        Permission.VIEW_SAVED_DASHBOARDS,
        Permission.VIEW_ALL_PROJECTS,
        Permission.ADD_MEMBERS,
        Permission.MODIFY_MEMBERS,
        Permission.DELETE_MEMBERS,
      ],
      [Role.ACCOUNTS]: [
        Permission.VIEW_ALLOCATIONS,
        Permission.VIEW_REVENUE,
        Permission.VIEW_COSTS,
        Permission.VIEW_ALL_COSTS,
        Permission.VIEW_DASHBOARD,
        Permission.VIEW_SAVED_DASHBOARDS,
        Permission.VIEW_ALL_PROJECTS,
      ],
    };

    return rolePermissionsMap[role] || [];
  }

  /**
   * Check if user has a specific role
   */
  hasRole(role: Role | string): boolean {
    if (role === Role.ADMIN && this.hasPbsRole('admin')) {
      return true;
    }
    if (role === Role.PROJECT_OWNER && this.hasPbsRole('project_owner')) {
      return true;
    }
    return this.role === role;
  }

  /**
   * Check if user has any of the specified roles
   */
  hasAnyRole(roles: (Role | string)[]): boolean {
    return roles.some((role) => this.hasRole(role));
  }

  /**
   * Get current user's role
   */
  getRole(): string {
    return this.role;
  }

  /**
   * Get all permissions
   */
  getPermissions(): string[] {
    return this.permissions;
  }

  /**
   * Check if a date is in the future (YYYY-MM format)
   */
  isFutureDate(dateStr: string): boolean {
    try {
      const [year, month] = dateStr.split('-').map(Number);
      const date = new Date(year, month - 1);
      const now = new Date();
      
      // Compare year and month only
      const dateYearMonth = date.getFullYear() * 12 + date.getMonth();
      const nowYearMonth = now.getFullYear() * 12 + now.getMonth();
      
      return dateYearMonth >= nowYearMonth;
    } catch (error) {
      return false;
    }
  }

  /**
   * Check if user can modify allocation based on date
   */
  canModifyAllocation(allocationDate: string): boolean {
    const isFuture = this.isFutureDate(allocationDate);
    
    // Operations can modify past and future
    if (this.hasPermission(Permission.MODIFY_PAST_ALLOCATIONS)) {
      return true;
    }
    
    // Project owners can only modify future
    if (isFuture && this.hasPermission(Permission.MODIFY_FUTURE_ALLOCATIONS)) {
      return true;
    }
    
    return false;
  }

  /**
   * Check if user can delete allocation based on date
   */
  canDeleteAllocation(allocationDate: string): boolean {
    const isFuture = this.isFutureDate(allocationDate);
    
    // Operations can delete past and future
    if (this.hasPermission(Permission.DELETE_PAST_ALLOCATIONS)) {
      return true;
    }
    
    // Project owners can only delete future
    if (isFuture && this.hasPermission(Permission.DELETE_FUTURE_ALLOCATIONS)) {
      return true;
    }
    
    return false;
  }

  /**
   * Get role display name
   */
  getRoleDisplayName(): string {
    const roleNames: Record<string, string> = {
      [Role.PROJECT_OWNER]: 'Project Owner',
      [Role.BU_HEAD]: 'BU Head',
      [Role.ADMIN]: 'Admin',
    //   [Role.OPERATIONS]: 'Operations',
    //   [Role.ACCOUNTS]: 'Accounts',
    };
    
    return roleNames[this.role] || this.role;
  }

  /**
   * Clear permissions (on logout)
   */
  clearPermissions(): void {
    this.permissions = [];
    this.role = Role.PROJECT_OWNER;
    this.permissionsLoaded = false;
  }

  /**
   * Initialize permissions on login
   */
  async initializePermissions(): Promise<void> {
    await this.loadPermissions();
  }

  hasPbsRole(role: string): boolean {
    if (!role) {
      return false;
    }
    if (this.pbsRoles.length === 0) {
      const user = authService.getUserData();
      if (user?.pbsRoles) {
        this.pbsRoles = user.pbsRoles;
      }
    }
    return this.pbsRoles.includes(role);
  }

  hasAnyPbsRole(roles: string[]): boolean {
    if (!roles || roles.length === 0) {
      return false;
    }
    return roles.some((role) => this.hasPbsRole(role));
  }

  getPbsRoles(): string[] {
    if (this.pbsRoles.length === 0) {
      const user = authService.getUserData();
      if (user?.pbsRoles) {
        this.pbsRoles = user.pbsRoles;
      }
    }
    return [...this.pbsRoles];
  }
}

// Export singleton instance
const permissionService = new PermissionService();
export default permissionService;


